import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { getAccessToken } from "../../../awsAuth";
import "./EditShowcase.scss";

import backIcon from "../img/back.png";
import mapIcon from "../img/map.png";
import addImageIcon from "../img/add-image.png";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const TYPE_LABEL = {
  services: "🛠️ Послуги",
  catalog: "📦 Каталог",
  portfolio: "🎨 Портфоліо",
  resume: "📄 Резюме",
};

function parseTypeFromId(showcaseId) {
  const raw = String(showcaseId || "");
  const idx = raw.lastIndexOf("__");
  if (idx === -1) return "services";
  const t = raw.slice(idx + 2);
  return t || "services";
}

function safeObj(v) {
  return v && typeof v === "object" ? v : {};
}

function normalizeContacts(contacts) {
  const c = safeObj(contacts);
  return {
    showPhone: typeof c.showPhone === "boolean" ? c.showPhone : false,
    phone: String(c.phone || ""),
    telegram: String(c.telegram || ""),
    instagram: String(c.instagram || ""),
    website: String(c.website || ""),
  };
}

function normalizeGallery(core) {
  const c = safeObj(core);
  const corePhotos = Array.isArray(c.photos) ? c.photos.slice(0, 5) : [];

  const gIn = safeObj(c.gallery);
  const title = String(gIn.title || "");

  const itemsIn = Array.isArray(gIn.items) ? gIn.items : [];
  const byUrl = new Map();

  for (const it of itemsIn) {
    const o = safeObj(it);
    const url = String(o.url || "").trim();
    if (!url) continue;
    byUrl.set(url, {
      url,
      caption: String(o.caption || ""),
      price: String(o.price || ""),
    });
  }

  const items = corePhotos.map((url) => {
    const u = String(url || "").trim();
    return (
      byUrl.get(u) || {
        url: u,
        caption: "",
        price: "",
      }
    );
  });

  return {
    title,
    items: items.slice(0, 5),
  };
}

function normalizeCoreByType(type, core) {
  const c = safeObj(core);
  const contacts = normalizeContacts(c.contacts);
  const experience = safeObj(c.experience);

  const corePhotos = Array.isArray(c.photos) ? c.photos.slice(0, 5) : [];
  const gallery = normalizeGallery({ ...c, photos: corePhotos });

  if (type === "services") {
    const modeRaw = String(c.mode || "").trim();
    const mode = modeRaw === "online" || modeRaw === "offline" || modeRaw === "both" ? modeRaw : "both";

    return {
      mode,
      subtitle: String(c.subtitle || ""),
      services: Array.isArray(c.services) ? c.services : [],
      location: safeObj(c.location),
      schedule: Array.isArray(c.schedule) ? c.schedule : [],
      offers: Array.isArray(c.offers) ? c.offers : [],
      faq: Array.isArray(c.faq) ? c.faq : [],
      contacts,
      photos: corePhotos,
      experience,
      gallery,
    };
  }

  return {
    ...c,
    contacts,
    photos: corePhotos,
    experience,
    gallery,
  };
}

const MAX_PROCESS_PHOTOS = 5;
const DEFAULT_WEBP_QUALITY = 0.82;
const DEFAULT_MAX_W = 1600;

const SCHEDULE_DAY_OPTIONS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд", "Пн-Пт", "Пн-Сб", "Сб-Нд", "Щодня", "Вихідний"];

const WORKING_HOURS_DAYS = [
  { key: "mon", label: "Пн" },
  { key: "tue", label: "Вт" },
  { key: "wed", label: "Ср" },
  { key: "thu", label: "Чт" },
  { key: "fri", label: "Пт" },
  { key: "sat", label: "Сб" },
  { key: "sun", label: "Нд" },
];

function makeTimeOptions(stepMinutes = 30) {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      out.push(`${hh}:${mm}`);
    }
  }
  out.push("00:00");
  return Array.from(new Set(out));
}
const TIME_OPTIONS = makeTimeOptions(30);

const DAY_LABEL_TO_KEY = {
  Пн: "mon",
  Вт: "tue",
  Ср: "wed",
  Чт: "thu",
  Пт: "fri",
  Сб: "sat",
  Нд: "sun",
};
const DAY_KEY_TO_LABEL = {
  mon: "Пн",
  tue: "Вт",
  wed: "Ср",
  thu: "Чт",
  fri: "Пт",
  sat: "Сб",
  sun: "Нд",
};

function normalizeTimeStr(v, fallback) {
  const s = String(v || "").trim();
  if (!s) return fallback;
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return fallback;
}

function scheduleArrayToHoursMap(scheduleArr) {
  const map = {
    mon: { enabled: false, start: "10:00", end: "18:00" },
    tue: { enabled: false, start: "10:00", end: "18:00" },
    wed: { enabled: false, start: "10:00", end: "18:00" },
    thu: { enabled: false, start: "10:00", end: "18:00" },
    fri: { enabled: false, start: "10:00", end: "18:00" },
    sat: { enabled: false, start: "10:00", end: "18:00" },
    sun: { enabled: false, start: "10:00", end: "18:00" },
  };

  const arr = Array.isArray(scheduleArr) ? scheduleArr : [];
  for (const row of arr) {
    const dLabel = String(row?.day || "").trim();
    const key = DAY_LABEL_TO_KEY[dLabel];
    if (!key) continue;

    const from = normalizeTimeStr(row?.from, map[key].start);
    const to = normalizeTimeStr(row?.to, map[key].end);

    map[key] = { enabled: true, start: from, end: to };
  }

  return map;
}

function hoursMapToScheduleArray(hoursMap) {
  const h = safeObj(hoursMap);

  const out = [];
  for (const d of WORKING_HOURS_DAYS) {
    const v = safeObj(h[d.key]);
    if (!v.enabled) continue;

    out.push({
      day: DAY_KEY_TO_LABEL[d.key],
      from: normalizeTimeStr(v.start, "10:00"),
      to: normalizeTimeStr(v.end, "18:00"),
    });
  }
  return out;
}

async function buildAuthHeaders() {
  const raw = await Promise.resolve(getAccessToken());
  const token = raw ? String(raw).trim() : "";
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    try {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = () => reject(new Error("FileReader error"));
      r.readAsDataURL(file);
    } catch (e) {
      reject(e);
    }
  });
}

function loadImg(src) {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Image load error"));
      img.src = src;
    } catch (e) {
      reject(e);
    }
  });
}

async function compressToWebpFile(file, { maxW = DEFAULT_MAX_W, quality = DEFAULT_WEBP_QUALITY } = {}) {
  try {
    const type = String(file?.type || "");
    if (!type.startsWith("image/")) return file;

    const dataUrl = await readFileAsDataURL(file);
    const img = await loadImg(dataUrl);

    const w0 = img.naturalWidth || img.width || 0;
    const h0 = img.naturalHeight || img.height || 0;
    if (!w0 || !h0) return file;

    const scale = w0 > maxW ? maxW / w0 : 1;
    const w = Math.max(1, Math.round(w0 * scale));
    const h = Math.max(1, Math.round(h0 * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return file;

    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), "image/webp", typeof quality === "number" ? quality : DEFAULT_WEBP_QUALITY);
      } catch {
        resolve(null);
      }
    });

    if (!blob) return file;

    const baseName = String(file?.name || "photo").replace(/\.[^/.]+$/, "");
    const webpFile = new File([blob], `${baseName}.webp`, { type: "image/webp" });

    return webpFile.size >= file.size ? file : webpFile;
  } catch {
    return file;
  }
}

export default function EditShowcase() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userId: currentUserId } = useAuth();

  const type = useMemo(() => parseTypeFromId(id), [id]);

  const [data, setData] = useState(null);
  const [core, setCore] = useState(() => normalizeCoreByType(type, {}));

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [phone, setPhone] = useState("");
  const [telegram, setTelegram] = useState("");
  const [instagram, setInstagram] = useState("");
  const [website, setWebsite] = useState("");
  const [showPhone, setShowPhone] = useState(false);

  const [coverUrl, setCoverUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmActionRef = useRef(null);

  const phoneTouchedRef = useRef(false);

  const [serviceDraft, setServiceDraft] = useState("");
  const [addingService, setAddingService] = useState(false);
  const serviceInputRef = useRef(null);

  const [offerDraft, setOfferDraft] = useState({ title: "", details: "" });
  const [addingOffer, setAddingOffer] = useState(false);
  const offerTitleRef = useRef(null);

  const [faqDraft, setFaqDraft] = useState({ q: "", a: "" });
  const [addingFaq, setAddingFaq] = useState(false);
  const faqQRef = useRef(null);

  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photosInputRef = useRef(null);
  const [activePhotoIdx, setActivePhotoIdx] = useState(-1);

  const [openSections, setOpenSections] = useState({
    basics: true,
    contacts: false,
    mode: false,
    experience: false,
    services: false,
    gallery: false,
    offers: false,
    location: false,
    schedule: false,
    faq: false,
  });

  const toggleSection = useCallback((key) => {
    if (key === "basics") return;
    setOpenSections((p) => ({ ...safeObj(p), [key]: !p?.[key] }));
  }, []);

  const isOwner = useMemo(() => {
    const ownerId = data?.userId || data?.ownerId;
    return !!currentUserId && !!ownerId && String(currentUserId) === String(ownerId);
  }, [currentUserId, data]);

  const toastShow = useCallback((msg) => {
    setToast(String(msg || ""));
    window.clearTimeout(toastShow._t);
    toastShow._t = window.setTimeout(() => setToast(""), 1800);
  }, []);
  toastShow._t = toastShow._t;

  const openConfirm = useCallback((actionFn) => {
    confirmActionRef.current = actionFn;
    setConfirmOpen(true);
  }, []);

  const closeConfirm = useCallback(() => {
    confirmActionRef.current = null;
    setConfirmOpen(false);
  }, []);

  const runConfirm = useCallback(() => {
    const fn = confirmActionRef.current;
    closeConfirm();
    if (typeof fn === "function") fn();
  }, [closeConfirm]);

  const load = useCallback(async () => {
    if (!id) return;

    setBusy(true);
    setErr("");

    try {
      const res = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(id)}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Помилка завантаження (${res.status})`);
      }

      const json = await res.json();
      const item = json?.item || json?.data || json;

      setData(item);

      setTitle(String(item?.title || ""));
      setDescription(String(item?.description || ""));
      setCoverUrl(String(item?.coverUrl || item?.cover || ""));

      const normalized = normalizeCoreByType(type, item?.core);
      setCore(normalized);

      const c = normalizeContacts(normalized?.contacts);

      phoneTouchedRef.current = false;

      setShowPhone(!!c.showPhone);
      setPhone(String(c.phone || ""));
      setTelegram(String(c.telegram || ""));
      setInstagram(String(c.instagram || ""));
      setWebsite(String(c.website || ""));

      setAddingService(false);
      setServiceDraft("");

      setAddingOffer(false);
      setOfferDraft({ title: "", details: "" });

      setAddingFaq(false);
      setFaqDraft({ q: "", a: "" });

      setActivePhotoIdx(-1);

      setOpenSections({
        basics: true,
        contacts: false,
        mode: false,
        experience: false,
        services: false,
        gallery: false,
        offers: false,
        location: false,
        schedule: false,
        faq: false,
      });
    } catch (e) {
      setErr(e?.message || "Не вдалося завантажити вітрину");
    } finally {
      setBusy(false);
    }
  }, [id, type]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!currentUserId) return;
    if (!id) return;
    if (!isOwner) return;

    let alive = true;

    (async () => {
      try {
        if (phoneTouchedRef.current) return;

        const phoneNow = String(phone || "").trim();
        if (phoneNow) return;

        const res = await fetch(`${API_BASE}/api/account/${encodeURIComponent(String(currentUserId))}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!res.ok) return;

        const json = await res.json().catch(() => ({}));
        const item = json?.item || json?.data || json;

        const accPhone = String(item?.phone || item?.phoneNumber || item?.phone_number || "").trim();

        if (!alive) return;
        if (phoneTouchedRef.current) return;

        if (accPhone && !String(phone || "").trim()) {
          setPhone(accPhone);
        }
      } catch {}
    })();

    return () => {
      alive = false;
    };
  }, [currentUserId, id, isOwner, phone]);

  const onCoverPick = useCallback(
    async (file) => {
      if (!file) return;
      if (!isOwner) return toastShow("Це не ваша вітрина");

      const fd = new FormData();
      fd.append("cover", file);

      setSaving(true);
      setErr("");

      try {
        const authHeaders = await buildAuthHeaders();
        const res = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(id)}/cover`, {
          method: "POST",
          body: fd,
          credentials: "include",
          headers: {
            ...authHeaders,
          },
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || `Помилка завантаження обкладинки (${res.status})`);
        }

        const json = await res.json();
        const url = json?.coverUrl || json?.url || json?.data?.coverUrl;

        if (url) {
          setCoverUrl(String(url));
          toastShow("Обкладинку оновлено");
        } else {
          toastShow("Завантажено, але URL не повернувся");
        }
      } catch (e) {
        setErr(e?.message || "Не вдалося завантажити обкладинку");
      } finally {
        setSaving(false);
      }
    },
    [id, isOwner, toastShow, currentUserId]
  );

  const onCoverInput = useCallback(
    (e) => {
      const f = e?.target?.files?.[0];
      if (f) onCoverPick(f);
      if (e?.target) e.target.value = "";
    },
    [onCoverPick]
  );

  const syncGalleryFromPhotos = useCallback(
    (prevCore, nextPhotos) => {
      const p = normalizeCoreByType(type, prevCore);

      const next = Array.isArray(nextPhotos) ? nextPhotos.slice(0, 5) : [];
      const g = safeObj(p.gallery);

      const itemsIn = Array.isArray(g.items) ? g.items : [];
      const byUrl = new Map();
      for (const it of itemsIn) {
        const o = safeObj(it);
        const url = String(o.url || "").trim();
        if (!url) continue;
        byUrl.set(url, {
          url,
          caption: String(o.caption || ""),
          price: String(o.price || ""),
        });
      }

      const items = next.map((url) => byUrl.get(String(url)) || { url: String(url), caption: "", price: "" });

      return {
        ...p,
        photos: next,
        gallery: {
          title: String(g.title || ""),
          items: items.slice(0, 5),
        },
      };
    },
    [type]
  );

  const onPhotosPick = useCallback(
    async (filesList) => {
      if (!isOwner) return toastShow("Це не ваша вітрина");
      const files = Array.from(filesList || []).filter(Boolean);
      if (!files.length) return;

      setErr("");
      setUploadingPhotos(true);

      try {
        const fd = new FormData();

        const picked = files.slice(0, MAX_PROCESS_PHOTOS);
        for (const f of picked) {
          const compressed = await compressToWebpFile(f, { maxW: DEFAULT_MAX_W, quality: DEFAULT_WEBP_QUALITY });
          fd.append("photos", compressed);
        }

        const authHeaders = await buildAuthHeaders();
        const res = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(id)}/photos`, {
          method: "POST",
          body: fd,
          credentials: "include",
          headers: {
            ...authHeaders,
          },
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || `Помилка завантаження фото (${res.status})`);
        }

        const json = await res.json().catch(() => ({}));
        const nextPhotos = Array.isArray(json?.photos) ? json.photos.slice(0, 5) : [];

        setCore((prev) => syncGalleryFromPhotos(prev, nextPhotos));
        toastShow("Фото додано");
        setActivePhotoIdx(-1);
      } catch (e) {
        setErr(e?.message || "Не вдалося завантажити фото");
      } finally {
        setUploadingPhotos(false);
      }
    },
    [id, isOwner, toastShow, currentUserId, syncGalleryFromPhotos]
  );

  const onPhotosInput = useCallback(
    (e) => {
      const files = e?.target?.files;
      if (files && files.length) onPhotosPick(files);
      if (e?.target) e.target.value = "";
    },
    [onPhotosPick]
  );

  const removePhoto = useCallback(
    async (url) => {
      if (!isOwner) return toastShow("Це не ваша вітрина");
      const u = String(url || "").trim();
      if (!u) return;

      setErr("");
      setUploadingPhotos(true);

      try {
        const authHeaders = await buildAuthHeaders();
        const res = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(id)}/photos`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          credentials: "include",
          body: JSON.stringify({ url: u }),
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || `Помилка видалення фото (${res.status})`);
        }

        const json = await res.json().catch(() => ({}));
        const nextPhotos = Array.isArray(json?.photos) ? json.photos.slice(0, 5) : [];

        setCore((prev) => syncGalleryFromPhotos(prev, nextPhotos));
        toastShow("Фото видалено");
        setActivePhotoIdx(-1);
      } catch (e) {
        setErr(e?.message || "Не вдалося видалити фото");
      } finally {
        setUploadingPhotos(false);
      }
    },
    [id, isOwner, toastShow, currentUserId, syncGalleryFromPhotos]
  );

  const onShare = useCallback(async () => {
    const url =
      typeof window !== "undefined" ? `${window.location.origin}/showcase/${id}` : `${API_BASE}/showcase/${id}`;

    try {
      await navigator.clipboard.writeText(url);
      toastShow("Посилання скопійовано");
    } catch {
      try {
        const el = document.createElement("textarea");
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        toastShow("Посилання скопійовано");
      } catch {
        toastShow("Не вдалося скопіювати");
      }
    }
  }, [id, toastShow]);

  const buildPayload = useCallback(() => {
    const nextCore = normalizeCoreByType(type, core);

    nextCore.contacts = {
      showPhone: !!showPhone,
      phone: String(phone || "").trim(),
      telegram: String(telegram || "").trim(),
      instagram: String(instagram || "").trim(),
      website: String(website || "").trim(),
    };

    const g = safeObj(nextCore.gallery);
    const itemsIn = Array.isArray(g.items) ? g.items : [];
    const items = itemsIn
      .map((it) => {
        const o = safeObj(it);
        return {
          url: String(o.url || "").trim(),
          caption: String(o.caption || ""),
          price: String(o.price || ""),
        };
      })
      .filter((it) => !!it.url)
      .slice(0, 5);

    nextCore.gallery = { title: String(g.title || "").trim(), items };
    nextCore.photos = items.map((it) => it.url).slice(0, 5);

    return {
      title: String(title || "").trim(),
      description: String(description || "").trim(),
      coverUrl: String(coverUrl || "").trim(),
      core: nextCore,
    };
  }, [title, description, coverUrl, core, type, showPhone, phone, telegram, instagram, website]);

  const onSave = useCallback(async () => {
    if (!isOwner) return toastShow("Це не ваша вітрина");

    setSaving(true);
    setErr("");

    try {
      const payload = buildPayload();

      const authHeaders = await buildAuthHeaders();
      const res = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Помилка збереження (${res.status})`);
      }

      toastShow("Збережено");
      navigate(`/showcase/${encodeURIComponent(id)}`);
      return;
    } catch (e) {
      setErr(e?.message || "Не вдалося зберегти");
    } finally {
      setSaving(false);
    }
  }, [id, isOwner, buildPayload, toastShow, load, currentUserId]);

  const setCoreField = useCallback((key, value) => {
    setCore((prev) => ({ ...safeObj(prev), [key]: value }));
  }, []);

  const addService = useCallback(() => {
    setAddingService(true);
    window.setTimeout(() => {
      try {
        serviceInputRef.current?.focus?.();
      } catch {}
    }, 0);
  }, []);

  const saveServiceDraft = useCallback(() => {
    const v = String(serviceDraft || "").trim();
    if (!v) return toastShow("Введіть назву послуги");

    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.services) ? p.services.slice() : [];
      arr.push(v);
      return { ...p, services: arr };
    });

    setServiceDraft("");
    setAddingService(false);
  }, [serviceDraft, toastShow]);

  const cancelServiceDraft = useCallback(() => {
    setServiceDraft("");
    setAddingService(false);
  }, []);

  const removeService = useCallback((idx) => {
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.services) ? p.services.slice() : [];
      arr.splice(idx, 1);
      return { ...p, services: arr };
    });
  }, []);

  const addOffer = useCallback(() => {
    setAddingOffer(true);
    window.setTimeout(() => {
      try {
        offerTitleRef.current?.focus?.();
      } catch {}
    }, 0);
  }, []);

  const saveOfferDraft = useCallback(() => {
    const t = String(offerDraft?.title || "").trim();
    const d = String(offerDraft?.details || "").trim();
    if (!t) return toastShow("Введіть заголовок пропозиції");

    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.offers) ? p.offers.slice() : [];
      arr.push({ title: t, details: d });
      return { ...p, offers: arr };
    });

    setOfferDraft({ title: "", details: "" });
    setAddingOffer(false);
  }, [offerDraft, toastShow]);

  const cancelOfferDraft = useCallback(() => {
    setOfferDraft({ title: "", details: "" });
    setAddingOffer(false);
  }, []);

  const updateOffer = useCallback((idx, patch) => {
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.offers) ? p.offers.slice() : [];
      const it = safeObj(arr[idx]);
      arr[idx] = { ...it, ...safeObj(patch) };
      return { ...p, offers: arr };
    });
  }, []);

  const removeOffer = useCallback((idx) => {
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.offers) ? p.offers.slice() : [];
      arr.splice(idx, 1);
      return { ...p, offers: arr };
    });
  }, []);

  const addFaq = useCallback(() => {
    setAddingFaq(true);
    window.setTimeout(() => {
      try {
        faqQRef.current?.focus?.();
      } catch {}
    }, 0);
  }, []);

  const saveFaqDraft = useCallback(() => {
    const q = String(faqDraft?.q || "").trim();
    const a = String(faqDraft?.a || "").trim();
    if (!q) return toastShow("Введіть питання");

    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.faq) ? p.faq.slice() : [];
      arr.push({ q, a });
      return { ...p, faq: arr };
    });

    setFaqDraft({ q: "", a: "" });
    setAddingFaq(false);
  }, [faqDraft, toastShow]);

  const cancelFaqDraft = useCallback(() => {
    setFaqDraft({ q: "", a: "" });
    setAddingFaq(false);
  }, []);

  const updateFaq = useCallback((idx, patch) => {
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.faq) ? p.faq.slice() : [];
      const it = safeObj(arr[idx]);
      arr[idx] = { ...it, ...safeObj(patch) };
      return { ...p, faq: arr };
    });
  }, []);

  const removeFaq = useCallback((idx) => {
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.faq) ? p.faq.slice() : [];
      arr.splice(idx, 1);
      return { ...p, faq: arr };
    });
  }, []);

  const addScheduleRow = useCallback(() => {
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.schedule) ? p.schedule.slice() : [];
      arr.push({ day: "Пн", from: "10:00", to: "18:00" });
      return { ...p, schedule: arr };
    });
  }, []);

  const updateScheduleRow = useCallback((idx, patch) => {
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.schedule) ? p.schedule.slice() : [];
      const it = safeObj(arr[idx]);
      arr[idx] = { ...it, ...safeObj(patch) };
      return { ...p, schedule: arr };
    });
  }, []);

  const removeScheduleRow = useCallback((idx) => {
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      const arr = Array.isArray(p.schedule) ? p.schedule.slice() : [];
      arr.splice(idx, 1);
      return { ...p, schedule: arr };
    });
  }, []);

  const locationObj = useMemo(() => safeObj(core?.location), [core]);
  const scheduleArr = useMemo(() => (Array.isArray(core?.schedule) ? core.schedule : []), [core]);
  const offersArr = useMemo(() => (Array.isArray(core?.offers) ? core.offers : []), [core]);
  const faqArr = useMemo(() => (Array.isArray(core?.faq) ? core.faq : []), [core]);
  const servicesArr = useMemo(() => (Array.isArray(core?.services) ? core.services : []), [core]);

  const workingHoursMap = useMemo(() => scheduleArrayToHoursMap(scheduleArr), [scheduleArr]);

  const setWorkingHoursMap = useCallback((nextMap) => {
    const nextSchedule = hoursMapToScheduleArray(nextMap);
    setCore((prev) => {
      const p = normalizeCoreByType("services", prev);
      return { ...p, schedule: nextSchedule };
    });
  }, []);

  const toggleWorkingDay = useCallback(
    (dayKey) => {
      const m = safeObj(workingHoursMap);
      const v = safeObj(m[dayKey]);

      const enabledNext = !v.enabled;

      const next = {
        ...m,
        [dayKey]: {
          enabled: enabledNext,
          start: normalizeTimeStr(v.start, "10:00"),
          end: normalizeTimeStr(v.end, "18:00"),
        },
      };

      if (enabledNext && (!v.start || !v.end)) {
        next[dayKey] = { enabled: true, start: "10:00", end: "18:00" };
      }

      setWorkingHoursMap(next);
    },
    [workingHoursMap, setWorkingHoursMap]
  );

  const setWorkingTime = useCallback(
    (dayKey, which, value) => {
      const val = normalizeTimeStr(value, which === "start" ? "10:00" : "18:00");
      const next = {
        ...workingHoursMap,
        [dayKey]: {
          ...safeObj(workingHoursMap[dayKey]),
          enabled: true,
          [which]: val,
        },
      };
      setWorkingHoursMap(next);
    },
    [workingHoursMap, setWorkingHoursMap]
  );

  const clearWorkingDay = useCallback(
    (dayKey) => {
      const next = {
        ...workingHoursMap,
        [dayKey]: { enabled: false, start: "10:00", end: "18:00" },
      };
      setWorkingHoursMap(next);
    },
    [workingHoursMap, setWorkingHoursMap]
  );

  const experienceValue = useMemo(() => safeObj(core?.experience), [core]);

  const galleryValue = useMemo(() => safeObj(core?.gallery), [core]);
  const galleryItems = useMemo(() => (Array.isArray(galleryValue?.items) ? galleryValue.items : []), [galleryValue]);
  const galleryTitle = useMemo(() => String(galleryValue?.title || ""), [galleryValue]);

  const setGalleryTitle = useCallback(
    (val) => {
      setCore((prev) => {
        const p = normalizeCoreByType(type, prev);
        const g = safeObj(p.gallery);
        return { ...p, gallery: { ...g, title: String(val || "") } };
      });
    },
    [type]
  );

  const updateGalleryItem = useCallback(
    (idx, patch) => {
      setCore((prev) => {
        const p = normalizeCoreByType(type, prev);
        const g = safeObj(p.gallery);
        const items = Array.isArray(g.items) ? g.items.slice() : [];
        const it = safeObj(items[idx]);
        items[idx] = { ...it, ...safeObj(patch) };
        const urls = items.map((x) => String(x?.url || "")).filter(Boolean).slice(0, 5);
        return { ...p, gallery: { ...g, items: items.slice(0, 5) }, photos: urls };
      });
    },
    [type]
  );

  const currentYear = useMemo(() => {
    try {
      return new Date().getFullYear();
    } catch {
      return 2026;
    }
  }, []);

  const startYearNum = useMemo(() => {
    const raw = String(experienceValue?.startYear ?? "").trim();
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return 0;
    return n;
  }, [experienceValue]);

  const expYears = useMemo(() => {
    if (!startYearNum) return 0;
    const diff = currentYear - startYearNum;
    return diff > 0 ? diff : 0;
  }, [currentYear, startYearNum]);

  const clientsCountNum = useMemo(() => {
    const raw = String(experienceValue?.clientsCount ?? "").trim();
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return 0;
    return n;
  }, [experienceValue]);

  const setExperienceField = useCallback(
    (key, value) => {
      setCore((prev) => {
        const p = normalizeCoreByType(type, prev);
        const ex = safeObj(p?.experience);
        return { ...p, experience: { ...ex, [key]: value } };
      });
    },
    [type]
  );

  return (
    <div className="editShowcase">
      <div className="esTopBar">
        <button className="esIconBtn" onClick={() => navigate(-1)} disabled={saving || busy}>
          <img src={backIcon} alt="Назад" />
        </button>

        <div className="esTopTitle">Редагування вітрини</div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button className="esIconBtn" onClick={() => onShare()} disabled={!id} style={{ display: "none" }}>
            <span style={{ fontWeight: 800 }}>⤴</span>
          </button>

          <button className="esSaveBtn" onClick={onSave} disabled={saving || busy || !isOwner} style={{ display: "none" }}>
            {saving ? "Зберігаю..." : "Зберегти"}
          </button>
        </div>
      </div>

      {toast ? <div className="esToast">{toast}</div> : null}

      <div className="esBody">
        {err ? (
          <div className="esCard">
            <div className="esCardHead">
              <div className="esCardTitle" style={{ color: "rgba(185, 28, 28, 0.95)" }}>
                Помилка
              </div>
            </div>
            <div className="esText">{err}</div>
          </div>
        ) : null}

        {busy && !data ? (
          <>
            <div className="esHero skel" style={{ height: 240 }} />
            <div className="esCard skel" style={{ height: 86 }} />
            <div className="esCard skel" style={{ height: 140 }} />
          </>
        ) : null}

        {!busy && data && !isOwner ? (
          <div className="esCard">
            <div className="esCardHead">
              <div className="esCardTitle">Доступ обмежено</div>
            </div>
            <div className="esText esTextMuted">Це не ваша вітрина. Редагування недоступне.</div>
          </div>
        ) : null}

        {!busy && (isOwner || !data) ? (
          <>
            <div className="esHero">
              {coverUrl ? (
                <img className="esHeroImg" src={coverUrl} alt="Обкладинка" />
              ) : (
                <div className="esHeroEmpty">
                  <div className="esHeroPhTitle">Обкладинка</div>
                  <div className="esHeroPhText">
                    Додайте фото зверху — воно формує перше враження. Краще: світле, чисте, з вашою роботою/послугою.
                  </div>
                </div>
              )}

              <label className="esHeroUpload">
                Змінити обкладинку
                <input type="file" accept="image/*" onChange={onCoverInput} />
              </label>
            </div>

            <div className="esCard">
              <div className="esCardHead">
                <div className="esCardTitle">Основне</div>
              </div>

              <div className="esCardBody is-open">
                <div className="esField">
                  <div className="esLabel">Назва</div>
                  <input
                    className="esInput"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Наприклад: Барбер, Масаж, Ремонт телефонів..."
                  />
                </div>

                <div className="esField">
                  <div className="esLabel">Опис</div>
                  <textarea
                    className="esTextarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Коротко: хто ви, що робите, чому до вас"
                  />
                </div>
              </div>
            </div>

            <div className="esCard">
  <div className="esCardHead">
    <button
      type="button"
      className="esCollapseBtn"
      onClick={() => toggleSection("contacts")}
      aria-expanded={!!openSections.contacts}
    >
      <span className={`esChevron ${openSections.contacts ? "is-open" : ""}`} />
      <div className="esCardTitle">Контакти</div>
    </button>
  </div>

  <div className={`esCardBody ${openSections.contacts ? "is-open" : ""}`}>
    <div className="esField">
      <div className="phone-visibility esGlassBlock">
        <div className="toggle-row">
          <div className="toggle-text">
            <div className="title">Показувати номер у вітрині</div>
            <div className={`state ${showPhone ? "on" : "off"}`}>
              {showPhone ? "УВІМКНЕНО" : "ВИМКНЕНО"}
            </div>
          </div>

          <label className={`pretty-switch ${showPhone ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={showPhone}
              onChange={(e) => setShowPhone(e.target.checked)}
              aria-label="Показувати номер у вітрині"
            />
            <span className="track">
              <span className="thumb" />
            </span>
          </label>
        </div>

        {showPhone ? (
          <input
            type="tel"
            className="pill-input"
            placeholder="Номер телефону"
            value={phone}
            onChange={(e) => {
              phoneTouchedRef.current = true;
              setPhone(e.target.value);
            }}
          />
        ) : (
          <div className="hint">
            Якщо вимкнено — гостям буде доступно тільки “Написати”.
          </div>
        )}
      </div>
    </div>

    {}
  </div>
</div>


            {type === "services" ? (
              <>
                <div className="esCard">
                  <div className="esCardHead">
                    <button type="button" className="esCollapseBtn" onClick={() => toggleSection("services")} aria-expanded={!!openSections.services}>
                      <span className={`esChevron ${openSections.services ? "is-open" : ""}`} />
                      <div className="esCardTitle">Послуги</div>
                    </button>

                    {openSections.services ? (
                      <button className="esAdd" type="button" onClick={addService}>
                        + Додати
                      </button>
                    ) : null}
                  </div>

                  <div className={`esCardBody ${openSections.services ? "is-open" : ""}`}>
                    {addingService ? (
                      <div className="esInlineAdd">
                        <div className="esField" style={{ marginTop: 0 }}>
                          <div className="esLabel">Назва послуги</div>
                          <input
                            ref={serviceInputRef}
                            className="esInput"
                            value={serviceDraft}
                            onChange={(e) => setServiceDraft(e.target.value)}
                            placeholder="Наприклад: Чоловіча стрижка, Масаж спини..."
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                saveServiceDraft();
                              }
                              if (e.key === "Escape") cancelServiceDraft();
                            }}
                          />
                        </div>

                        <div className="esInlineActions">
                          <button className="esGhost" type="button" onClick={cancelServiceDraft}>
                            Скасувати
                          </button>
                          <button className="esPrimary" type="button" onClick={saveServiceDraft}>
                            Додати
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {servicesArr.length === 0 ? (
                      <div className="esPhantom">
                        <div className="esPhantomTitle">Порожньо</div>
                        <div className="esPhantomText">Додай 3–8 ключових послуг. Це підніме конверсію і робить вітрину зрозумілою з першого погляду.</div>
                      </div>
                    ) : (
                      <div className="esList">
                        {servicesArr.map((s, idx) => (
                          <div className="esItem" key={`${s}_${idx}`}>
                            <div className="esItemTop">
                              <div className="esItemTitle">{String(s)}</div>
                              <div className="esItemActions">
                                <button className="esMini esMini--danger" type="button" onClick={() => openConfirm(() => removeService(idx))}>
                                  Видалити
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="esCard">
                  <div className="esCardHead">
                    <button type="button" className="esCollapseBtn" onClick={() => toggleSection("gallery")} aria-expanded={!!openSections.gallery}>
                      <span className={`esChevron ${openSections.gallery ? "is-open" : ""}`} />
                      <div className="esCardTitle">{galleryTitle ? galleryTitle : "Фото робіт"}</div>
                    </button>

                    {openSections.gallery ? (
                      <button
                        className="esAdd"
                        type="button"
                        onClick={() => {
                          try {
                            photosInputRef.current?.click?.();
                          } catch {}
                        }}
                        disabled={uploadingPhotos}
                      >
                        {uploadingPhotos ? "Завантажую..." : "+ Додати фото"}
                      </button>
                    ) : null}
                  </div>

                  <div className={`esCardBody ${openSections.gallery ? "is-open" : ""}`}>
                    <div>
                      <div className="esText esTextMuted">
                        Підказка: додай до 5 фото — процес роботи, готові результати, атмосфера тощо. Назву блоку можна зробити будь-яку.
                      </div>

                      <div className="esField">
                        <div className="esLabel">Назва блоку (опційно)</div>
                        <input
                          className="esInput"
                          value={galleryTitle}
                          onChange={(e) => setGalleryTitle(e.target.value)}
                          placeholder="Наприклад: Процес / Роботи / Атмосфера"
                        />
                      </div>

                      <input ref={photosInputRef} type="file" accept="image/*" multiple onChange={onPhotosInput} style={{ display: "none" }} />

                      {galleryItems.length === 0 ? (
                        <button
                          type="button"
                          className="esGalleryEmptyAdd"
                          onClick={() => {
                            try {
                              photosInputRef.current?.click?.();
                            } catch {}
                          }}
                          disabled={uploadingPhotos}
                        >
                          <div className="esGalleryEmptyIconWrap">
                            <img src={addImageIcon} alt="Додати фото" />
                          </div>
                        </button>
                      ) : (
                        <div className="esGalleryRowWrap">
                          <div className="esGalleryRow">
                            {galleryItems.slice(0, 5).map((it, idx) => {
                              const url = String(it?.url || "");
                              const isActive = idx === activePhotoIdx;

                              return (
                                <div className="esGalleryTileWrap" key={`tile_${url}_${idx}`}>
                                  <button
                                    type="button"
                                    className={`esGalleryTile ${isActive ? "is-active" : ""}`}
                                    onClick={() => setActivePhotoIdx((p) => (p === idx ? -1 : idx))}
                                    title="Натисни, щоб редагувати"
                                    disabled={!url}
                                  >
                                    {url ? <img className="esGalleryTileImg" src={url} alt="Фото" /> : null}
                                  </button>

                                  <button
                                    type="button"
                                    className="esGalleryRemove"
                                    onClick={() => openConfirm(() => removePhoto(url))}
                                    disabled={uploadingPhotos}
                                    title="Видалити"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}

                            {galleryItems.length < 5 ? (
                              <button
                                type="button"
                                className="esGalleryAddTile"
                                onClick={() => {
                                  try {
                                    photosInputRef.current?.click?.();
                                  } catch {}
                                }}
                                disabled={uploadingPhotos}
                                title="Додати фото"
                              >
                                <img src={addImageIcon} alt="Додати" />
                              </button>
                            ) : null}
                          </div>

                          {activePhotoIdx >= 0 && galleryItems[activePhotoIdx] ? (
                            <div className="esGalleryEditor">
                              <div className="esField" style={{ marginTop: 0 }}>
                                <div className="esLabel">Підпис (опційно)</div>
                                <input
                                  className="esInput"
                                  value={String(galleryItems[activePhotoIdx]?.caption || "")}
                                  onChange={(e) => updateGalleryItem(activePhotoIdx, { caption: e.target.value })}
                                  placeholder="Наприклад: До/після, процес, деталі..."
                                />
                              </div>

                              <div className="esField">
                                <div className="esLabel">Ціна (опційно)</div>
                                <input
                                  className="esInput"
                                  inputMode="numeric"
                                  value={String(galleryItems[activePhotoIdx]?.price || "")}
                                  onChange={(e) => {
                                    const v = String(e.target.value || "").replace(/[^\d]/g, "");
                                    updateGalleryItem(activePhotoIdx, { price: v });
                                  }}
                                  placeholder="Наприклад: 500"
                                />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}

                      {false ? (
                        <div className="esGalleryGrid">
                          {galleryItems.slice(0, 5).map((it, idx) => {
                            const url = String(it?.url || "");
                            return (
                              <div className="esGalleryCard" key={`g_${url}_${idx}`}>
                                <div className="esGalleryThumb">{url ? <img src={url} alt="Фото" /> : null}</div>

                                <div className="esGalleryFields">
                                  <div className="esField" style={{ marginTop: 0 }}>
                                    <div className="esLabel">Підпис (опційно)</div>
                                    <input
                                      className="esInput"
                                      value={String(it?.caption || "")}
                                      onChange={(e) => updateGalleryItem(idx, { caption: e.target.value })}
                                      placeholder="Наприклад: До/після, процес, деталі..."
                                    />
                                  </div>

                                  <div className="esField">
                                    <div className="esLabel">Ціна (опційно)</div>
                                    <input
                                      className="esInput"
                                      inputMode="numeric"
                                      value={String(it?.price || "")}
                                      onChange={(e) => {
                                        const v = String(e.target.value || "").replace(/[^\d]/g, "");
                                        updateGalleryItem(idx, { price: v });
                                      }}
                                      placeholder="Наприклад: 500"
                                    />
                                  </div>

                                  <div className="esGalleryActions">
                                    <button className="esMini esMini--danger" type="button" onClick={() => openConfirm(() => removePhoto(url))} disabled={uploadingPhotos}>
                                      Видалити фото
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="esCard">
                  <div className="esCardHead">
                    <button type="button" className="esCollapseBtn" onClick={() => toggleSection("offers")} aria-expanded={!!openSections.offers}>
                      <span className={`esChevron ${openSections.offers ? "is-open" : ""}`} />
                      <div className="esCardTitle">Акційні пропозиції</div>
                    </button>

                    {openSections.offers ? (
                      <button className="esAdd" type="button" onClick={addOffer}>
                        + Додати
                      </button>
                    ) : null}
                  </div>

                  <div className={`esCardBody ${openSections.offers ? "is-open" : ""}`}>
                    {addingOffer ? (
                      <div className="esInlineAdd">
                        <div className="esField" style={{ marginTop: 0 }}>
                          <div className="esLabel">Заголовок</div>
                          <input
                            ref={offerTitleRef}
                            className="esInput"
                            value={String(offerDraft?.title || "")}
                            onChange={(e) => setOfferDraft((p) => ({ ...safeObj(p), title: e.target.value }))}
                            placeholder="Наприклад: -20% для нових клієнтів"
                          />
                        </div>

                        <div className="esField">
                          <div className="esLabel">Деталі (опційно)</div>
                          <textarea
                            className="esTextarea"
                            value={String(offerDraft?.details || "")}
                            onChange={(e) => setOfferDraft((p) => ({ ...safeObj(p), details: e.target.value }))}
                            placeholder="Умови, термін, що входить"
                          />
                        </div>

                        <div className="esInlineActions">
                          <button className="esGhost" type="button" onClick={cancelOfferDraft}>
                            Скасувати
                          </button>
                          <button className="esPrimary" type="button" onClick={saveOfferDraft}>
                            Додати
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {offersArr.length === 0 ? (
                      <div className="esText esTextMuted">Немає. Додай 1–2 пропозиції: “-20% для нових”, “Подарунок”, “Комплекс”.</div>
                    ) : (
                      <div className="esList">
                        {offersArr.map((o, idx) => (
                          <div className="esItem" key={`offer_${idx}`}>
                            <div className="esItemTop">
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="esItemTitle">{String(o?.title || "Пропозиція")}</div>

                                <div className="esField">
                                  <div className="esLabel">Заголовок</div>
                                  <input
                                    className="esInput"
                                    value={String(o?.title || "")}
                                    onChange={(e) => updateOffer(idx, { title: e.target.value })}
                                    placeholder="Наприклад: Знижка 20% для нових клієнтів"
                                  />
                                </div>

                                <div className="esField">
                                  <div className="esLabel">Деталі</div>
                                  <textarea
                                    className="esTextarea"
                                    value={String(o?.details || "")}
                                    onChange={(e) => updateOffer(idx, { details: e.target.value })}
                                    placeholder="Умови, термін, що входить"
                                  />
                                </div>
                              </div>

                              <div className="esItemActions">
                                <button className="esMini esMini--danger" type="button" onClick={() => openConfirm(() => removeOffer(idx))}>
                                  Видалити
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="esCard">
                  <div className="esCardHead">
                    <button type="button" className="esCollapseBtn" onClick={() => toggleSection("location")} aria-expanded={!!openSections.location}>
                      <span className={`esChevron ${openSections.location ? "is-open" : ""}`} />
                      <div className="esCardTitle">Локація</div>
                    </button>
                  </div>

                  <div className={`esCardBody ${openSections.location ? "is-open" : ""}`}>
                    <div className="esField">
                      <div className="esLabel">Місто</div>
                      <input
                        className="esInput"
                        value={String(locationObj?.city || "")}
                        onChange={(e) => setCoreField("location", { ...locationObj, city: e.target.value })}
                        placeholder="Наприклад: Київ"
                      />
                    </div>

                    <div className="esField">
                      <div className="esLabel">Адреса</div>
                      <input
                        className="esInput"
                        value={String(locationObj?.address || "")}
                        onChange={(e) => setCoreField("location", { ...locationObj, address: e.target.value })}
                        placeholder="Вулиця, будинок (опційно)"
                      />
                    </div>

                    <div className="esField">
                      <div className="esLabel">Пояснення</div>
                      <input
                        className="esInput"
                        value={String(locationObj?.note || "")}
                        onChange={(e) => setCoreField("location", { ...locationObj, note: e.target.value })}
                        placeholder="Наприклад: 2 поверх, біля метро..."
                      />
                    </div>

                    {false ? (
                      <div className="esMapPh">
                        <div className="esMapPh__grid" />
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="esCard">
                  <div className="esCardHead">
                    <button type="button" className="esCollapseBtn" onClick={() => toggleSection("schedule")} aria-expanded={!!openSections.schedule}>
                      <span className={`esChevron ${openSections.schedule ? "is-open" : ""}`} />
                      <div className="esCardTitle">Робочий графік</div>
                    </button>
                  </div>

                  <div className={`esCardBody ${openSections.schedule ? "is-open" : ""}`}>
                    <div className="esText esTextMuted" style={{ marginBottom: 10 }}>
                      Якщо не вказати графік — за замовчуванням вважається, що ви доступні цілодобово.
                    </div>

                    <div className="esWorkingDaysRow">
                      {WORKING_HOURS_DAYS.map((d) => {
                        const v = safeObj(workingHoursMap[d.key]);
                        const active = !!v.enabled;
                        return (
                          <button key={d.key} type="button" className={`esWorkingDayPill ${active ? "is-active" : ""}`} onClick={() => toggleWorkingDay(d.key)}>
                            {d.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="esWorkingRows">
                      {WORKING_HOURS_DAYS.map((d) => {
                        const v = safeObj(workingHoursMap[d.key]);
                        const active = !!v.enabled;

                        return (
                          <div className="esWorkingRow" key={`row_${d.key}`}>
                            <div className="esWorkingRowLeft">
                              <div className="esWorkingDayLabel">{d.label}</div>
                            </div>

                            <div className="esWorkingRowMain">
                              {active ? (
                                <div className="esWorkingTimeRow">
                                  <button type="button" className="esWorkingTrash" title="Очистити день" onClick={() => clearWorkingDay(d.key)}>
                                    ❌
                                  </button>

                                  <select className="esWorkingTimeSelect" value={normalizeTimeStr(v.start, "10:00")} onChange={(e) => setWorkingTime(d.key, "start", e.target.value)}>
                                    {TIME_OPTIONS.map((t) => (
                                      <option value={t} key={`s_${d.key}_${t}`}>
                                        {t}
                                      </option>
                                    ))}
                                  </select>

                                  <div className="esWorkingArrow">→</div>

                                  <select className="esWorkingTimeSelect" value={normalizeTimeStr(v.end, "18:00")} onChange={(e) => setWorkingTime(d.key, "end", e.target.value)}>
                                    {TIME_OPTIONS.map((t) => (
                                      <option value={t} key={`e_${d.key}_${t}`}>
                                        {t}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ) : (
                                <div className="esWorkingOff">Вихідний</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {false ? (
                      <div style={{ marginTop: 14 }}>
                        <div className="esText esTextMuted">
                          Заповни як тобі зручно: “Пн-Пт 10:00–18:00”, або окремими днями. Якщо вихідний — можеш написати “Вихідний”
                          (або “Зачинено”) у полі “День”.
                        </div>

                        <button className="esAdd" type="button" onClick={addScheduleRow}>
                          + Додати рядок
                        </button>

                        {scheduleArr.length === 0 ? (
                          <div className="esPhantom">
                            <div className="esPhantomTitle">Порожньо</div>
                            <div className="esPhantomText">Додай хоча б 1 рядок — це одразу піднімає довіру і конверсію.</div>
                          </div>
                        ) : (
                          <div className="esList">
                            {scheduleArr.map((row, idx) => {
                              const day = String(row?.day || "");
                              const from = String(row?.from || "");
                              const to = String(row?.to || "");

                              return (
                                <div className="esItem" key={`sch_${idx}`}>
                                  <div className="esItemTop">
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div className="esItemTitle">{day ? `Графік: ${day}` : "Графік"}</div>

                                      <div className="esField" style={{ marginTop: 10 }}>
                                        <div className="esLabel">День</div>

                                        <input
                                          className="esInput"
                                          value={day}
                                          onChange={(e) => updateScheduleRow(idx, { day: e.target.value })}
                                          placeholder="Наприклад: Пн-Пт"
                                          list={`sch_day_list_${idx}`}
                                        />
                                        <datalist id={`sch_day_list_${idx}`}>
                                          {SCHEDULE_DAY_OPTIONS.map((opt) => (
                                            <option value={opt} key={`opt_${idx}_${opt}`} />
                                          ))}
                                        </datalist>
                                      </div>

                                      <div className="esField">
                                        <div className="esLabel">Час</div>
                                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                          <input className="esInput" value={from} onChange={(e) => updateScheduleRow(idx, { from: e.target.value })} placeholder="10:00" inputMode="numeric" style={{ maxWidth: 160 }} />
                                          <input className="esInput" value={to} onChange={(e) => updateScheduleRow(idx, { to: e.target.value })} placeholder="18:00" inputMode="numeric" style={{ maxWidth: 160 }} />
                                        </div>
                                        <div className="esText esTextMuted" style={{ marginTop: 6 }}>
                                          Якщо це вихідний — просто залиш час порожнім, а в “День” напиши “Вихідний/Зачинено”.
                                        </div>
                                      </div>
                                    </div>

                                    <div className="esItemActions">
                                      <button className="esMini esMini--danger" type="button" onClick={() => openConfirm(() => removeScheduleRow(idx))}>
                                        Видалити
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="esCard">
                  <div className="esCardHead">
                    <button type="button" className="esCollapseBtn" onClick={() => toggleSection("mode")} aria-expanded={!!openSections.mode}>
                      <span className={`esChevron ${openSections.mode ? "is-open" : ""}`} />
                      <div className="esCardTitle">Формат роботи</div>
                    </button>
                  </div>

                  <div className={`esCardBody ${openSections.mode ? "is-open" : ""}`}>
                    <div className="esModeRow">
                      <button className={`esModePill ${core?.mode === "online" ? "is-active" : ""}`} onClick={() => setCoreField("mode", "online")} type="button">
                        Онлайн
                      </button>
                      <button className={`esModePill ${core?.mode === "offline" ? "is-active" : ""}`} onClick={() => setCoreField("mode", "offline")} type="button">
                        Офлайн
                      </button>
                      <button className={`esModePill ${core?.mode === "both" ? "is-active" : ""}`} onClick={() => setCoreField("mode", "both")} type="button">
                        Онлайн + офлайн
                      </button>
                    </div>

                    <div className="esField">
                      <div className="esLabel">Короткий підпис</div>
                      <input className="esInput" value={String(core?.subtitle || "")} onChange={(e) => setCoreField("subtitle", e.target.value)} placeholder="Наприклад: Працюю за записом, 10:00–18:00" />
                    </div>
                  </div>
                </div>

                <div className="esCard">
                  <div className="esCardHead">
                    <button type="button" className="esCollapseBtn" onClick={() => toggleSection("experience")} aria-expanded={!!openSections.experience}>
                      <span className={`esChevron ${openSections.experience ? "is-open" : ""}`} />
                      <div className="esCardTitle">Досвід</div>
                    </button>
                  </div>

                  <div className={`esCardBody ${openSections.experience ? "is-open" : ""}`}>
                    <div className="esField">
                      <div className="esLabel">Працюю з</div>
                      <input
                        className="esInput"
                        inputMode="numeric"
                        value={String(experienceValue?.startYear ?? "")}
                        onChange={(e) => {
                          const v = String(e.target.value || "").replace(/[^\d]/g, "").slice(0, 4);
                          setExperienceField("startYear", v);
                        }}
                        placeholder="Наприклад: 2021"
                      />
                      <div className="esText esTextMuted" style={{ marginTop: 6 }}>
                        {startYearNum ? `Досвід: ${expYears} ${expYears === 1 ? "рік" : expYears >= 2 && expYears <= 4 ? "роки" : "років"}` : "Досвід порахується автоматично"}
                      </div>
                    </div>

                    <div className="esField">
                      <div className="esLabel">Клієнтів (приблизно)</div>
                      <div className="esSuffixRow">
                        <input
                          className="esInput"
                          inputMode="numeric"
                          value={String(experienceValue?.clientsCount ?? "")}
                          onChange={(e) => {
                            const v = String(e.target.value || "").replace(/[^\d]/g, "");
                            setExperienceField("clientsCount", v);
                          }}
                          placeholder="Наприклад: 200"
                        />
                        <div className="esSuffix">+</div>
                      </div>

                      <div className="esText esTextMuted" style={{ marginTop: 6 }}>
                        {clientsCountNum ? `Показ у вітрині: ${clientsCountNum}+` : "Введи число — “+” додасться автоматично"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="esCard">
                  <div className="esCardHead">
                    <button type="button" className="esCollapseBtn" onClick={() => toggleSection("faq")} aria-expanded={!!openSections.faq}>
                      <span className={`esChevron ${openSections.faq ? "is-open" : ""}`} />
                      <div className="esCardTitle">Найпопулярніші запитання</div>
                    </button>

                    {openSections.faq ? (
                      <button className="esAdd" type="button" onClick={addFaq}>
                        + Додати
                      </button>
                    ) : null}
                  </div>

                  <div className={`esCardBody ${openSections.faq ? "is-open" : ""}`}>
                    {addingFaq ? (
                      <div className="esInlineAdd">
                        <div className="esField" style={{ marginTop: 0 }}>
                          <div className="esLabel">Питання</div>
                          <input
                            ref={faqQRef}
                            className="esInput"
                            value={String(faqDraft?.q || "")}
                            onChange={(e) => setFaqDraft((p) => ({ ...safeObj(p), q: e.target.value }))}
                            placeholder="Наприклад: Скільки коштує?"
                            onKeyDown={(e) => {
                              if (e.key === "Escape") cancelFaqDraft();
                            }}
                          />
                        </div>

                        <div className="esField">
                          <div className="esLabel">Відповідь (опційно)</div>
                          <textarea className="esTextarea" value={String(faqDraft?.a || "")} onChange={(e) => setFaqDraft((p) => ({ ...safeObj(p), a: e.target.value }))} placeholder="Коротко і по суті" />
                        </div>

                        <div className="esInlineActions">
                          <button className="esGhost" type="button" onClick={cancelFaqDraft}>
                            Скасувати
                          </button>
                          <button className="esPrimary" type="button" onClick={saveFaqDraft}>
                            Додати
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {faqArr.length === 0 ? (
                      <div className="esText esTextMuted">Немає. Додай 3–5 питань: “Ціна?”, “Запис?”, “Передоплата?”, “Виїзд?”.</div>
                    ) : (
                      <div className="esList">
                        {faqArr.map((f, idx) => (
                          <div className="esItem" key={`faq_${idx}`}>
                            <div className="esItemTop">
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="esItemTitle">{String(f?.q || "Питання")}</div>

                                <div className="esField">
                                  <div className="esLabel">Питання</div>
                                  <input className="esInput" value={String(f?.q || "")} onChange={(e) => updateFaq(idx, { q: e.target.value })} placeholder="Наприклад: Скільки коштує?" />
                                </div>

                                <div className="esField">
                                  <div className="esLabel">Відповідь</div>
                                  <textarea className="esTextarea" value={String(f?.a || "")} onChange={(e) => updateFaq(idx, { a: e.target.value })} placeholder="Коротко і по суті" />
                                </div>
                              </div>

                              <div className="esItemActions">
                                <button className="esMini esMini--danger" type="button" onClick={() => openConfirm(() => removeFaq(idx))}>
                                  Видалити
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}

            <div className="esSaveFooter">
              <button className="esBottomSave" onClick={onSave} disabled={saving || busy || !isOwner} type="button">
                {saving ? "Зберігаю..." : "Зберегти"}
              </button>
            </div>

            <div className="esBottomSpace" />
          </>
        ) : null}
      </div>

      <div className="esBottomBar" style={{ display: "none" }}>
        <div className="esBottomBar__inner">
          <button className="esBottomSave" onClick={onSave} disabled={saving || busy || !isOwner} type="button">
            {saving ? "Зберігаю..." : "Зберегти"}
          </button>
        </div>
      </div>

      {confirmOpen ? (
        <div className="esOverlay" onClick={closeConfirm}>
          <div className="esModal" onClick={(e) => e.stopPropagation()}>
            <div className="esModalTop">
              <div className="esModalTitle">Підтвердження</div>
              <button className="esIconBtn" onClick={closeConfirm} style={{ width: 36, height: 36 }}>
                <span style={{ fontWeight: 800, lineHeight: 1 }}>×</span>
              </button>
            </div>

            <div className="esModalBody">
              <div className="esText">Ви впевнені, що хочете видалити цей елемент?</div>
            </div>

            <div className="esModalFooter">
              <button className="esGhost" type="button" onClick={closeConfirm}>
                Скасувати
              </button>
              <button className="esPrimary" type="button" onClick={runConfirm}>
                Так, видалити
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
